import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, EyeOff, Eye, Flag, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const btn = "mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1";

export default function ReviewsAdminTab() {
  const [filter, setFilter] = useState("reported");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const load = () => {
    const params = { q };
    if (filter === "reported") params.reported = true;
    if (filter === "removed") params.status = "removed";
    api.get("/admin/reviews", { params }).then((r) => setData(r.data)).catch(() => setData({ reviews: [], counts: {} }));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id, action) => {
    if (action === "delete" && !window.confirm("Permanently delete this review?")) return;
    try { await api.post(`/admin/reviews/${id}/action`, { action }); toast.success("Done"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-reviews-tab">
      <div className="flex flex-wrap gap-2 items-center">
        {[["reported", `Reported · ${data?.counts?.reported ?? "…"}`], ["all", `All · ${data?.counts?.total ?? "…"}`], ["removed", `Removed · ${data?.counts?.removed ?? "…"}`]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-reviews-filter-${k}`}
            className={`${btn} ${filter === k ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-white/50"}`}>{l}</button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="ml-auto flex items-center gap-2">
          <Search className="w-4 h-4 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text, app, member" data-testid="admin-reviews-search" className="bg-black/40 border border-white/10 rounded-full px-3 py-1.5 text-sm" />
        </form>
      </div>
      {!data ? <div className="mono text-xs text-[#00F0FF] animate-pulse">Loading…</div> : data.reviews.length === 0 ? <div className="text-white/40 text-sm">No reviews in this view.</div> : (
        <div className="space-y-2">
          {data.reviews.map((r) => (
            <div key={r.id} className="glass rounded-2xl p-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <b>{r.user_name}</b><span className="text-white/40">on</span><span className="text-[#00F0FF] mono text-xs">{r.app_slug}</span>
                <span className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? "fill-[#FFE300] text-[#FFE300]" : "text-white/20"}`} />)}</span>
                {r.reports_count > 0 && <span className="mono text-[10px] text-[#FF003C] flex items-center gap-1"><Flag className="w-3 h-3" />{r.reports_count} reports</span>}
                {r.status === "removed" && <span className="mono text-[10px] text-white/40">REMOVED</span>}
                <span className="ml-auto mono text-[10px] text-white/40">{r.created_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              {r.title && <div className="font-bold mt-1">{r.title}</div>}
              <p className="text-white/70 text-sm">{r.body}</p>
              {r.reports?.length > 0 && <div className="mono text-[10px] text-white/40 mt-1">Reasons: {r.reports.map((x) => x.reason).join(" · ")}</div>}
              <div className="flex gap-2 mt-3">
                {r.status === "visible" ? <button onClick={() => act(r.id, "remove")} data-testid={`admin-review-remove-${r.id}`} className={`${btn} border-[#FF003C]/40 text-[#FF003C]`}><EyeOff className="w-3 h-3" /> Remove</button>
                  : <button onClick={() => act(r.id, "restore")} data-testid={`admin-review-restore-${r.id}`} className={`${btn} border-[#39FF14]/40 text-[#39FF14]`}><Eye className="w-3 h-3" /> Restore</button>}
                {r.reports_count > 0 && <button onClick={() => act(r.id, "clear_reports")} className={`${btn} border-white/15 text-white/60`}>Dismiss reports</button>}
                <button onClick={() => act(r.id, "delete")} data-testid={`admin-review-delete-${r.id}`} className={`${btn} border-white/15 text-white/60`}><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
